import type { ReactNode } from "react";
import { Bookmark, ExternalLink, Heart, ImageIcon, MessageCircle, Send } from "lucide-react";
import type { MessageDescriptor } from "react-intl";
import { useIntl } from "react-intl";

import { cn } from "@/lib/utils";
import { Badge } from "@runcheetah/ui";

export type InstagramPostCardPreviewAsset = {
  signedUrl: string;
  width: number | null;
  height: number | null;
};

export type ImportedInstagramPostCardMedia = {
  mediaType: string | null;
  mediaProductType?: string | null;
  caption: string | null;
  permalink: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  publishedAt: Date | string | null;
  insightsJson: Record<string, number> | null;
  previewAsset: InstagramPostCardPreviewAsset | null;
};

type ImportedInstagramPostImage = {
  src: string;
  width: number;
  height: number;
};

type InstagramPostMetricKey = "reach" | "total_interactions";

const importedFormatDescriptors = {
  story: {
    id: "socialPostCard.format.story",
    defaultMessage: "Instagram Story",
  },
  carousel: {
    id: "socialPostCard.format.carousel",
    defaultMessage: "Carousel",
  },
  reel: {
    id: "socialPostCard.format.reel",
    defaultMessage: "Reel",
  },
  video: {
    id: "socialPostCard.format.video",
    defaultMessage: "Video",
  },
  feed: {
    id: "socialPostCard.format.feedPost",
    defaultMessage: "Feed post",
  },
  tiktokPost: {
    id: "socialPostCard.format.tiktokPost",
    defaultMessage: "TikTok post",
  },
  tiktokCarousel: {
    id: "socialPostCard.format.tiktokCarousel",
    defaultMessage: "TikTok carousel",
  },
} as const satisfies Record<string, MessageDescriptor>;

export function getSocialPostPreviewCardFormatDescriptor(
  postType: string | null | undefined,
): MessageDescriptor {
  if (postType === "story") {
    return importedFormatDescriptors.story;
  }

  if (postType === "carousel_portrait" || postType === "carousel_square") {
    return importedFormatDescriptors.carousel;
  }

  if (postType === "tiktok_carousel_portrait") {
    return importedFormatDescriptors.tiktokCarousel;
  }

  if (postType === "tiktok_portrait") {
    return importedFormatDescriptors.tiktokPost;
  }

  return importedFormatDescriptors.feed;
}

export function getSocialPostPreviewCardHashtagSummary(
  hashtags: string[],
  maxVisible = 5,
): { visibleText: string; overflowCount: number } {
  const visibleHashtags = hashtags
    .slice(0, maxVisible)
    .map((tag) => `#${tag.replace(/^#/, "")}`)
    .join(" ");

  return {
    visibleText: visibleHashtags,
    overflowCount: Math.max(0, hashtags.length - maxVisible),
  };
}

export function getSocialPostPreviewCardCaptionText(caption: string, fallback: string): string {
  const trimmedCaption = caption.trim();

  if (trimmedCaption) {
    return trimmedCaption;
  }

  return fallback.trim();
}

export function getImportedInstagramPostFormatDescriptor(input: {
  mediaType: string | null;
  mediaProductType?: string | null;
}): MessageDescriptor {
  if (input.mediaProductType === "STORY" || input.mediaProductType === "STORIES") {
    return importedFormatDescriptors.story;
  }

  if (input.mediaProductType === "REELS") {
    return importedFormatDescriptors.reel;
  }

  if (input.mediaType === "CAROUSEL_ALBUM") {
    return importedFormatDescriptors.carousel;
  }

  if (input.mediaType === "VIDEO") {
    return importedFormatDescriptors.video;
  }

  return importedFormatDescriptors.feed;
}

export function resolveImportedInstagramPostImage(
  media: Pick<ImportedInstagramPostCardMedia, "mediaUrl" | "thumbnailUrl" | "previewAsset">,
): ImportedInstagramPostImage | null {
  if (media.previewAsset) {
    return {
      src: media.previewAsset.signedUrl,
      width: media.previewAsset.width ?? 1080,
      height: media.previewAsset.height ?? 1350,
    };
  }

  const fallbackUrl = media.thumbnailUrl ?? media.mediaUrl;
  if (!fallbackUrl) {
    return null;
  }

  return {
    src: fallbackUrl,
    width: 1080,
    height: 1350,
  };
}

export function getInstagramPostMetricValue(
  insights: Record<string, number> | null | undefined,
  key: InstagramPostMetricKey,
): number | null {
  const value = insights?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function InstagramPostVisualFallback({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="flex aspect-4/5 items-center justify-center bg-[#f6f3ee] p-8 text-center">
      <div className="max-w-[14rem]">
        <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#ebe7e0] text-[#7a766e]">
          <ImageIcon className="h-5 w-5" aria-hidden={true} />
        </span>
        <p className="mt-4 text-[13px] font-semibold leading-5 text-[#1f1f22]">{title}</p>
        {description ? (
          <p className="mt-1 text-[12px] leading-5 text-[#7a766e]">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

export function InstagramPostCard({
  formatLabel,
  timeLabel,
  profileLabel,
  caption,
  hashtags = [],
  visual,
  approvalLabel,
  showApprovalBadge = false,
  muted = false,
  onOpen,
  onEdit,
  mediaOverlay,
  className,
  footer,
}: {
  formatLabel: string;
  timeLabel: string;
  profileLabel: string;
  caption: string;
  hashtags?: string[];
  visual: ReactNode;
  approvalLabel?: string;
  showApprovalBadge?: boolean;
  muted?: boolean;
  onOpen?: () => void;
  onEdit?: () => void;
  mediaOverlay?: ReactNode;
  className?: string;
  footer?: ReactNode;
}) {
  const intl = useIntl();
  const hashtagSummary = getSocialPostPreviewCardHashtagSummary(hashtags);

  return (
    <article
      className={cn(
        "relative mx-auto w-full overflow-hidden rounded-2xl border border-[#ddd6cc] bg-[#fffefa] text-[#0f0f10] shadow-[0_4px_20px_rgba(15,15,16,0.08)]",
        onOpen && "cursor-pointer",
        muted && "opacity-70 grayscale",
        className,
      )}
      onClick={onOpen}
    >
      <div className="flex items-center justify-between border-b border-black/[0.055] bg-[linear-gradient(180deg,#fffefa,#fff8f1)] px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden">
            <img
              src="/instagram.svg"
              alt=""
              className="h-full w-full object-cover"
              aria-hidden={true}
            />
          </span>
          <span className="text-[12px] font-semibold leading-none text-[#0f0f10]">
            {formatLabel}
          </span>
        </div>
        <span className="shrink-0 pl-2 text-[11px] text-[#737373]">{timeLabel}</span>
      </div>

      <div className="group relative">
        {showApprovalBadge && approvalLabel ? (
          <div className="absolute left-2 top-2 z-10">
            <Badge
              shape="pill"
              variant="outline"
              className={cn(
                "rounded-full border-transparent px-2 py-0.5 text-[10px] font-medium shadow-sm backdrop-blur-sm",
                muted ? "bg-amber-100/85 text-amber-900/70" : "bg-white/80 text-foreground/65",
              )}
            >
              {approvalLabel}
            </Badge>
          </div>
        ) : null}
        {visual}
        {mediaOverlay ? (
          mediaOverlay
        ) : onEdit ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/0 opacity-0 transition-[background-color,opacity] duration-200 group-hover:bg-black/16 group-hover:opacity-100 group-focus-within:bg-black/16 group-focus-within:opacity-100">
            <button
              type="button"
              className="pointer-events-auto inline-flex h-9 items-center justify-center rounded-full bg-background/96 px-4 text-sm font-medium text-foreground shadow-lg transition-colors hover:bg-background"
              onClick={(event) => {
                event.stopPropagation();
                onEdit();
              }}
            >
              {intl.formatMessage({ id: "common.edit", defaultMessage: "Edit" })}
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-3.5 border-t border-black/[0.05] px-3 py-2">
        <Heart className="h-[18px] w-[18px] stroke-[1.7] text-[#1b1b1d]" aria-hidden={true} />
        <MessageCircle
          className="h-[18px] w-[18px] stroke-[1.7] text-[#1b1b1d]"
          aria-hidden={true}
        />
        <Send className="h-[18px] w-[18px] stroke-[1.7] text-[#1b1b1d]" aria-hidden={true} />
        <Bookmark
          className="ml-auto h-[18px] w-[18px] stroke-[1.7] text-[#1b1b1d]"
          aria-hidden={true}
        />
      </div>

      <div className="px-3 pb-3">
        <p className="line-clamp-2 text-[12.5px] leading-[1.5] text-[#1f1f22]">
          <span className="font-semibold">{profileLabel}</span> {caption}
        </p>
        {hashtags.length > 0 ? (
          <p className="mt-1 line-clamp-1 text-[11.5px] font-medium text-[#0b54d6]">
            {hashtagSummary.visibleText}
            {hashtagSummary.overflowCount > 0 ? (
              <span className="text-[#0b54d6]/60"> +{hashtagSummary.overflowCount}</span>
            ) : null}
          </p>
        ) : null}
        {footer ? <div className="mt-3">{footer}</div> : null}
      </div>
    </article>
  );
}

export function ImportedInstagramPostCard({
  media,
  profileLabel,
  className,
}: {
  media: ImportedInstagramPostCardMedia;
  profileLabel: string;
  className?: string;
}) {
  const intl = useIntl();
  const image = resolveImportedInstagramPostImage(media);
  const caption = getSocialPostPreviewCardCaptionText(
    media.caption ?? "",
    intl.formatMessage({
      id: "socialPostCard.captionUnavailable",
      defaultMessage: "Caption unavailable",
    }),
  );
  const timeLabel = media.publishedAt
    ? intl.formatDate(new Date(media.publishedAt), { month: "short", day: "numeric" })
    : intl.formatMessage({
        id: "socialPostCard.timeUnknown",
        defaultMessage: "Unknown date",
      });
  const formatLabel = intl.formatMessage(getImportedInstagramPostFormatDescriptor(media));
  const visual = image ? (
    <img
      src={image.src}
      alt={caption}
      width={image.width}
      height={image.height}
      className="block w-full object-cover"
      style={{ aspectRatio: `${image.width} / ${image.height}` }}
      loading="lazy"
    />
  ) : (
    <InstagramPostVisualFallback
      title={intl.formatMessage({
        id: "socialPostCard.previewUnavailableTitle",
        defaultMessage: "Preview unavailable",
      })}
      description={intl.formatMessage({
        id: "socialPostCard.previewUnavailableDescription",
        defaultMessage: "Instagram did not provide a media preview for this post.",
      })}
    />
  );

  return (
    <InstagramPostCard
      className={className}
      formatLabel={formatLabel}
      timeLabel={timeLabel}
      profileLabel={profileLabel}
      caption={caption}
      visual={visual}
      footer={<ImportedInstagramPostCardFooter media={media} />}
    />
  );
}

function ImportedInstagramPostCardFooter({ media }: { media: ImportedInstagramPostCardMedia }) {
  const intl = useIntl();
  // TODO: Restore insight metrics after instagram_business_manage_insights is approved.
  if (!media.permalink) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-black/[0.05] pt-3">
      <a
        href={media.permalink}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0b54d6] hover:underline"
      >
        {intl.formatMessage({
          id: "socialPostCard.viewOnInstagram",
          defaultMessage: "View on Instagram",
        })}
        <ExternalLink className="h-3 w-3" aria-hidden={true} />
      </a>
    </div>
  );
}
